import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePriceGroup } from 'core/contexts/priceGroup';
import { Form } from 'react-final-form';
import { Box, Button, Checkbox, FormControlLabel, FormHelperText, Grid2, Typography } from '@mui/material';
import { RadioButtonChecked, RadioButtonUnchecked } from '@mui/icons-material';
import Title from '../../../components/Title';
import ActionButtons from '../ActionButtons';

const CheckboxButton = ({ label, description, checked, handleCheck, removeCheck, disabled = false }) => {
    const handleClick = (event) => {
        event?.preventDefault();

        if (!checked) {
            handleCheck();
            return;
        }
        removeCheck();
    };

    const handleCheckboxChange = (event) => {
        if (event?.target?.checked) {
            handleCheck();
            return;
        }
        removeCheck();
    };

    return (
        <Button
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                mb: 2,
                width: '100%',
                padding: '12px 8px',
                border: `solid ${checked ? '2px var(--color-utriper-blue)' : '1px #13131340'}`,
                borderRadius: '8px',
                boxSizing: 'border-box',
                textTransform: 'none',
            }}
            onClick={handleClick}
            disabled={disabled}
        >
            <FormControlLabel
                control={<Checkbox checked={checked} onChange={handleCheckboxChange} name={label} icon={<RadioButtonUnchecked sx={{ color: 'var(--color-utriper-blue)' }} />} checkedIcon={<RadioButtonChecked sx={{ color: 'var(--color-utriper-blue)' }} />} />}
                label={label}
                sx={{
                    '& .MuiTypography-root': {
                        fontSize: '18px',
                        color: '#131313',
                    },
                }}
            />
            <Typography component="p" fontSize={14} fontWeight={400} textAlign="left" sx={{ pl: 4, pb: 1 }}>
                {description}
            </Typography>
        </Button>
    );
};

const SettingsForm = () => {
    const { t } = useTranslation();
    const translate = (key) => t(`price_group_wizard.settings_form.${key}`);
    const { CONFIG_TYPES, configType, handleSettings, handleNext, handleBack } = usePriceGroup();
    const labelManual = translate('label_manual');
    const descriptionManual = translate('description_manual');
    const labelExcel = (
        <>
            {translate('label_excel')} - <small>{translate('soon')}</small>
        </>
    );
    const descriptionExcel = translate('description_excel');
    const [submitted, setSubmitted] = useState(false);

    const validateForm = (values) => {
        const errors = {};
        if (!Number.isInteger(values?.configType)) {
            errors.configType = translate('validate_form.missing_config_type');
        }
        const validTypes = [CONFIG_TYPES.MANUAL, CONFIG_TYPES.EXCEL];
        if (Number.isInteger(values.configType) && !validTypes.includes(values.configType)) {
            errors.configType = translate('validate_form.invalid_config_type');
        }

        return errors;
    };

    const handleSubmit = ({ configType }) => {
        handleSettings(configType);
        handleNext();
    };

    return (
        <Form
            onSubmit={handleSubmit}
            initialValues={{ configType }}
            validate={validateForm}
            render={({ errors, values, handleSubmit, form }) => {
                const isManual = values?.configType === CONFIG_TYPES.MANUAL;
                const isExcel = values?.configType === CONFIG_TYPES.EXCEL;
                const { configType: configTypeError } = errors;

                let hasError = false;

                if (configTypeError) {
                    hasError = true;
                }

                const handleCheckManual = () => {
                    form.change('configType', CONFIG_TYPES.MANUAL);
                };

                const removeCheckManual = () => {
                    if (isManual) {
                        form.change('configType', null);
                        return;
                    }
                };

                const handleCheckExcel = () => {
                    form.change('configType', CONFIG_TYPES.EXCEL);
                };

                const removeCheckExcel = () => {
                    if (isExcel) {
                        form.change('configType', null);
                        return;
                    }
                };

                return (
                    <form onSubmit={handleSubmit} noValidate style={{ width: '100%', maxWidth: '480px' }}>
                        <Box component="section">
                            <Title title={translate('title')} description={translate('description')} />

                            {/* Form */}
                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                                {/* Manual */}
                                <CheckboxButton label={labelManual} description={descriptionManual} checked={isManual} handleCheck={handleCheckManual} removeCheck={removeCheckManual} />

                                {/* Excel */}
                                <CheckboxButton label={labelExcel} description={descriptionExcel} checked={isExcel} handleCheck={handleCheckExcel} removeCheck={removeCheckExcel} disabled={true} />

                                {submitted && hasError && <FormHelperText error={hasError}>{configTypeError}</FormHelperText>}
                            </Grid2>

                            <ActionButtons
                                handleSubmit={(e) => {
                                    setSubmitted(true);
                                    return handleSubmit(e);
                                }}
                                handleBack={handleBack}
                            />
                        </Box>
                    </form>
                );
            }}
        />
    );
};

export default SettingsForm;
